import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Headphones, Mail, Phone, MessageCircle, Clock, FileText, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerSupport = () => {
  const [customContent, setCustomContent] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data.customer_support_content) {
          setCustomContent(res.data.customer_support_content);
        }
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-44 pb-12 bg-gradient-to-br from-[#003456] to-[#001d30]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#fdc31c] rounded-full flex items-center justify-center mx-auto mb-6">
            <Headphones className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Customer Support</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            We're here to help you get the most out of your HomeLyph freeze dryer.
          </p>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {customContent ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
              <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: customContent }} />
            </div>
          ) : (
            <>
              {/* Contact Methods */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
                  <div className="w-14 h-14 bg-[#dbeef5] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-7 w-7 text-[#003456]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0a0203] mb-2">Call Us</h3>
                  <p className="text-gray-600 mb-4">Speak directly with our support team</p>
                  <a href="tel:1-888-223-1435" className="text-[#003456] font-semibold text-lg">1-888-223-1435</a>
                  <p className="text-gray-500 text-sm mt-2">Mon-Fri, 9am-4pm MST</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
                  <div className="w-14 h-14 bg-[#dbeef5] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-7 w-7 text-[#003456]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0a0203] mb-2">Email Us</h3>
                  <p className="text-gray-600 mb-4">Monday-Friday responses</p>
                  <a href="mailto:support@homelyph.com" className="text-[#003456] font-semibold">support@homelyph.com</a>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-sm text-center relative">
                  <div className="absolute top-4 right-4 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                    Coming Soon
                  </div>
                  <div className="w-14 h-14 bg-[#dbeef5] rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="h-7 w-7 text-[#003456]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0a0203] mb-2">Live Chat</h3>
                  <p className="text-gray-600 mb-4">Get instant answers to your questions</p>
                  <Button className="bg-gray-300 text-gray-500 cursor-not-allowed" disabled>
                    Start Chat
                  </Button>
                  <p className="text-gray-500 text-sm mt-2">Available at launch</p>
                </div>
              </div>
              
              {/* Support Resources */}
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Quick Resources</h2>
                  <div className="space-y-4">
                    <Link to="/documents" className="flex items-center gap-4 p-4 rounded-xl bg-[#f0f4f8] hover:bg-[#dbeef5] transition-colors">
                      <FileText className="h-6 w-6 text-[#003456]" />
                      <div>
                        <h4 className="font-semibold text-[#0a0203]">User Manuals & Guides</h4>
                        <p className="text-gray-500 text-sm">Download product documentation</p>
                      </div>
                    </Link>
                    <Link to="/recipes" className="flex items-center gap-4 p-4 rounded-xl bg-[#f0f4f8] hover:bg-[#dbeef5] transition-colors">
                      <FileText className="h-6 w-6 text-[#003456]" />
                      <div>
                        <h4 className="font-semibold text-[#0a0203]">Recipe Collection</h4>
                        <p className="text-gray-500 text-sm">Explore freeze-drying recipes</p>
                      </div>
                    </Link>
                    <Link to="/warranty" className="flex items-center gap-4 p-4 rounded-xl bg-[#f0f4f8] hover:bg-[#dbeef5] transition-colors">
                      <FileText className="h-6 w-6 text-[#003456]" />
                      <div>
                        <h4 className="font-semibold text-[#0a0203]">Warranty Information</h4>
                        <p className="text-gray-500 text-sm">Coverage details & claims</p>
                      </div>
                    </Link>
                    <a href="https://freezedryersupport.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 rounded-xl bg-[#f0f4f8] hover:bg-[#dbeef5] transition-colors">
                      <ExternalLink className="h-6 w-6 text-[#003456]" />
                      <div>
                        <h4 className="font-semibold text-[#0a0203]">Customer Portal</h4>
                        <p className="text-gray-500 text-sm">Track orders, manage account</p>
                      </div>
                    </a>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Support Hours</h2>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <Clock className="h-5 w-5 text-[#003456]" />
                      <div>
                        <h4 className="font-semibold text-[#0a0203]">Phone Support</h4>
                        <p className="text-gray-500">Monday - Friday: 9:00 AM - 4:00 PM MST</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Clock className="h-5 w-5 text-[#003456]" />
                      <div>
                        <h4 className="font-semibold text-[#0a0203]">Email Support</h4>
                        <p className="text-gray-500">Monday - Friday responses</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Clock className="h-5 w-5 text-[#003456]" />
                      <div>
                        <h4 className="font-semibold text-[#0a0203]">Live Chat</h4>
                        <p className="text-gray-500">Coming soon</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 p-4 bg-[#fdc31c]/10 rounded-xl">
                    <h4 className="font-semibold text-[#0a0203] mb-2">Holiday Hours</h4>
                    <p className="text-gray-600 text-sm">
                      Support hours may vary during Canadian and US holidays. Check our website or contact us for updated hours.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* FAQ Link */}
              <div className="bg-[#003456] rounded-2xl p-8 text-center">
                <h2 className="text-2xl font-bold text-white mb-4">Have Questions?</h2>
                <p className="text-white/80 mb-6 max-w-xl mx-auto">
                  Check out our comprehensive FAQ section or browse our blog for tips and troubleshooting guides.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to="/blog">
                    <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203]">
                      Browse Blog
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456]">
                      Contact Form
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CustomerSupport;
